import React from 'react';
import { BlogPost } from '../data/types';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Clock } from 'lucide-react';
import BlogImage from './BlogImage';

interface Props {
  post: BlogPost;
}

// Type guard to check if post has category
const hasCategory = (post: BlogPost): post is BlogPost & { category: string } => {
  return 'category' in post && typeof post.category === 'string';
};

// Type guard to check if post has tags
const hasTags = (post: BlogPost): post is BlogPost & { tags: string[] } => {
  return 'tags' in post && Array.isArray(post.tags);
};

const BlogCard: React.FC<Props> = ({ post }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  return (
    <Link 
      to={`/blog/${post.slug}`}
      className="group block h-full"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700 h-full flex flex-col">
        {/* Image Container */}
        <div className="relative overflow-hidden">
          <BlogImage
            src={post.image}
            title={post.title}
            category={hasCategory(post) ? post.category : undefined}
            alt={post.title}
            className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          
          {/* Optional Category Badge - Only show if category exists */}
          {hasCategory(post) && (
            <div className="absolute top-3 left-3">
              <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-700 dark:text-gray-300 text-xs font-medium px-2 py-1 rounded-full">
                {post.category}
              </span>
            </div>
          )}
        </div>

        {/* Content Container */}
        <div className="p-6 flex-1 flex flex-col">
          {/* Metadata */}
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3 flex-wrap">
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(post.publishedAt)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{getReadingTime(post.content)}</span>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
            {post.title}
          </h2>

          {/* Excerpt */}
          <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 flex-1">
            {post.excerpt}
          </p>

          {/* Optional Tags - Only show if tags exist */}
          {hasTags(post) && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.slice(0, 3).map((tag: string, index: number) => (
                <span 
                  key={index}
                  className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs px-2 py-1 rounded-full"
                >
                  #{tag}
                </span>
              ))}
              {post.tags.length > 3 && (
                <span className="text-gray-500 dark:text-gray-400 text-xs px-2 py-1">
                  +{post.tags.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Read More Link */}
          <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-gray-700">
            <span className="text-purple-600 dark:text-purple-400 font-semibold text-sm group-hover:underline">
              Read more
            </span>
            <ArrowRight className="w-4 h-4 text-purple-600 dark:text-purple-400 transform group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BlogCard;